import { useMemo } from 'react';

import { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { type ComparePaneInput } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';

import { assetsModel } from '@/entities/assets';
import { conditionModel } from '@/entities/condition';
import { IOXRayImagesInterfaceModel } from '@/entities/IOXRayImagesMatrix';
import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { getComparePaneInput } from '../lib';

const NO_ASSET_IDS: string[] = [];

// Null until ListReportPanoramasWithConditions answered for the report
export const useComparePaneInput = (
  reportID: string,
): ComparePaneInput | null => {
  const report = useAppSelector(reportsModel.selectors.selectByID(reportID));
  const panoramaIDs =
    useAppSelector(reportsModel.selectors.selectPanoramaAssetIDs(reportID)) ??
    NO_ASSET_IDS;
  const assetEntities = useAppSelector(assetsModel.selectors.selectEntities);

  // One selector instance per report, so unchanged inputs keep their identity
  const selectImageInterfaces = useMemo(
    () =>
      IOXRayImagesInterfaceModel.selectors.selectAllImageInterfaceByReport(
        reportID,
      ),
    [reportID],
  );
  const imageInterfaces = useAppSelector(selectImageInterfaces);

  const selectConditions = useMemo(
    () =>
      conditionModel.selectors.selectMaskConditionsByAssets({
        reportID,
        assetIDs: panoramaIDs,
      }),
    [reportID, panoramaIDs],
  );
  const conditions = useAppSelector(selectConditions);
  const selectTeeth = useMemo(
    () => toothModel.selectors.selectByReportID(reportID),
    [reportID],
  );
  const teeth = useAppSelector(selectTeeth);
  const featureFlags = useAppSelector(
    organizationModel.selectors.selectRelevantFeatureList,
  );

  return useMemo(() => {
    if (!report || panoramaIDs === NO_ASSET_IDS) {
      return null;
    }
    const panoramas = panoramaIDs
      .map((assetID) => assetEntities[assetID])
      .filter((asset): asset is Asset => Boolean(asset));

    return getComparePaneInput({
      report,
      panoramas,
      imageInterfaces,
      conditions,
      teeth,
      featureFlags,
    });
  }, [
    report,
    panoramaIDs,
    assetEntities,
    imageInterfaces,
    conditions,
    teeth,
    featureFlags,
  ]);
};
